"use client";

import * as React from "react";
import { usePathname } from "next/navigation";
import { MessageCircle, X, Users } from "lucide-react";
import { cn } from "@/lib/cn";

// Ẩn bong bóng "Chat với admin" trong khu vực ứng dụng (nó che các nút thao tác).
// Vẫn giữ ở trang giới thiệu/marketing để khách liên hệ.
const HIDE_ON_PREFIXES = [
  "/tao-anh-ai",
  "/bang-dieu-khien",
  "/lop-hoc",
  "/tai-lieu",
  "/tro-choi",
  "/tro-ly-ai",
  "/gioi-thieu",
  "/kho-icon",
  "/kho-tai-nguyen",
  "/mon-hoc",
  "/cong-dong",
  "/quan-tri",
];

/** Nhãn "Zalo" trong ô tròn xanh — logo chính thức là chữ, nên vẽ lại đơn giản. */
function ZaloMark() {
  return (
    <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[#0068FF] text-[10px] font-bold italic text-white">
      Zalo
    </span>
  );
}

function FacebookMark() {
  return (
    <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[#1877F2] text-white">
      <svg viewBox="0 0 24 24" className="size-5" fill="currentColor" aria-hidden="true">
        <path d="M14 8.5V7c0-.7.3-1 1-1h1.5V3.2C16 3.1 15 3 14 3c-2.2 0-3.7 1.3-3.7 3.7v1.8H8V11h2.3v8h2.9v-8h2l.3-2.5H14z" />
      </svg>
    </span>
  );
}

function CommunityMark({ tone }: { tone: string }) {
  return (
    <span className={cn("grid size-9 shrink-0 place-items-center rounded-full text-white", tone)}>
      <Users className="size-[18px]" aria-hidden="true" />
    </span>
  );
}

type Contact = { label: string; href: string | undefined; icon: React.ReactNode };

// process.env.NEXT_PUBLIC_* phải viết tường minh để Next inline lúc build.
const CONTACTS: Contact[] = [
  { label: "Zalo", href: process.env.NEXT_PUBLIC_CONTACT_ZALO, icon: <ZaloMark /> },
  { label: "Facebook", href: process.env.NEXT_PUBLIC_CONTACT_FACEBOOK, icon: <FacebookMark /> },
  {
    label: "Cộng đồng Zalo",
    href: process.env.NEXT_PUBLIC_CONTACT_ZALO_COMMUNITY,
    icon: <CommunityMark tone="bg-[#0068FF]" />,
  },
  {
    label: "Cộng đồng Facebook",
    href: process.env.NEXT_PUBLIC_CONTACT_FACEBOOK_COMMUNITY,
    icon: <CommunityMark tone="bg-[#1877F2]" />,
  },
];

export function ContactWidget() {
  const [open, setOpen] = React.useState(false);
  const pathname = usePathname();
  const items = CONTACTS.filter((c): c is Contact & { href: string } => Boolean(c.href));

  // Ẩn trong khu vực app (che nút thao tác) hoặc khi chưa cấu hình kênh nào.
  const hidden = HIDE_ON_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
  if (hidden || items.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2.5 sm:bottom-6 sm:right-6">
      {/* Danh sách kênh — hiện khi mở */}
      <ul
        className={cn(
          "flex flex-col items-end gap-2.5 transition-all duration-200 ease-out",
          open ? "pointer-events-auto opacity-100" : "pointer-events-none translate-y-2 opacity-0"
        )}
        aria-hidden={!open}
      >
        {items.map((c, i) => (
          <li
            key={c.label}
            className={cn("transition-all duration-200 ease-out", open ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0")}
            style={{ transitionDelay: open ? `${i * 45}ms` : "0ms" }}
          >
            <a
              href={c.href}
              target="_blank"
              rel="noopener noreferrer"
              tabIndex={open ? 0 : -1}
              className="flex items-center gap-2.5 rounded-full border border-border bg-surface py-1.5 pl-1.5 pr-4 text-body-sm font-semibold text-foreground shadow-lg transition-transform hover:-translate-y-0.5 hover:shadow-xl focus-visible:outline-none focus-visible:ring focus-visible:ring-ring"
            >
              {c.icon}
              <span className="whitespace-nowrap">{c.label}</span>
            </a>
          </li>
        ))}
      </ul>

      {/* Nút bật/tắt */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Đóng liên hệ" : "Chat với admin"}
        className="inline-flex items-center gap-2 rounded-full bg-red-600 py-2.5 pl-3 pr-4 text-body-sm font-semibold text-white shadow-lg transition-all hover:bg-red-700 hover:shadow-xl focus-visible:outline-none focus-visible:ring focus-visible:ring-red-400 focus-visible:ring-offset-2"
      >
        {open ? <X className="size-5" aria-hidden="true" /> : <MessageCircle className="size-5" aria-hidden="true" />}
        <span className="whitespace-nowrap">Chat với admin</span>
      </button>
    </div>
  );
}
